import React, { useState } from 'react';
import { useAssessment } from '../../context/AssessmentContext';
import ProgressPath from '../ui/ProgressPath';
import ParticleEffect from '../ui/ParticleEffect';
import AnimatedBackground from '../ui/AnimatedBackground';
import { Flower2Icon, LeafIcon, WavesIcon, ShieldIcon } from 'lucide-react';
const SensitivityAssessment: React.FC = () => {
  const {
    currentStep,
    setCurrentStep,
    assessmentData,
    updateAssessmentData
  } = useAssessment();
  const [sensitivityLevel, setSensitivityLevel] = useState<number>(assessmentData.sensitivityLevel || 50);
  const handleSensitivityChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSensitivityLevel(parseInt(e.target.value));
  };
  const handleContinue = () => {
    updateAssessmentData({
      sensitivityLevel
    });
    setCurrentStep(currentStep + 1);
  };
  // Determine sensitivity description based on level
  const getSensitivityDescription = () => {
    if (sensitivityLevel < 30) {
      return "You tend to be grounded and resilient in most situations. External stimuli don't overwhelm you easily.";
    } else if (sensitivityLevel < 70) {
      return "You experience a balanced sensitivity. Sometimes you're deeply affected by environments and emotions, while other times you're more resilient.";
    } else {
      return 'You likely experience the world deeply and intensely. Your sensitivity allows you to notice subtleties others miss, though it can sometimes feel overwhelming.';
    }
  };
  return <div className="min-h-screen flex flex-col items-center justify-center px-4 py-12 relative" data-id="element-738">
      <AnimatedBackground type="cosmic" data-id="element-739" />
      <ParticleEffect count={50} data-id="element-740" />
      {/* Decorative elements */}
      <div className="absolute top-10 left-10 garden-leaf opacity-20" data-id="element-741">
        <LeafIcon size={80} className="text-green-200" data-id="element-742" />
      </div>
      <div className="absolute bottom-20 right-20 garden-leaf opacity-20" data-id="element-743">
        <LeafIcon size={100} className="text-green-200" data-id="element-744" />
      </div>
      <div className="absolute top-1/4 right-1/4 garden-flower opacity-20" data-id="element-745">
        <Flower2Icon size={60} className="text-pink-200" data-id="element-746" />
      </div>
      <div className="w-full max-w-4xl" data-id="element-747">
        <h1 className="text-4xl md:text-5xl font-bold text-center text-white mb-2" data-id="element-748">
          <span className="text-pink-300" data-id="element-749">Seven</span>After
        </h1>
        <ProgressPath totalSteps={7} currentStep={1} data-id="element-750" />
        <div className="bg-white bg-opacity-10 backdrop-blur-md rounded-2xl p-6 md:p-8 border border-white border-opacity-20 shadow-xl relative overflow-hidden" data-id="element-751">
          <div className="spiral-pattern absolute inset-0 opacity-20 z-0" data-id="element-752"></div>
          <div className="lotus-pattern absolute inset-0 opacity-20 z-0" data-id="element-753"></div>
          <h2 className="text-2xl md:text-3xl font-bold text-center text-white mb-4 relative z-10" data-id="element-754">
            Your Sensitivity Landscape
          </h2>
          <p className="text-white text-center mb-6 relative z-10" data-id="element-755">
            How sensitive do you tend to be to environments, emotions, and
            experiences? This helps us calibrate your healing journey.
          </p>
          <div className="bg-white bg-opacity-20 rounded-xl p-6 mb-6 relative z-10" data-id="element-756">
            <div className="flex justify-between items-center mb-2" data-id="element-757">
              <div className="flex items-center" data-id="element-758">
                <ShieldIcon className="text-blue-300 mr-2" size={20} data-id="element-759" />
                <span className="text-white text-opacity-80" data-id="element-760">
                  Less Sensitive
                </span>
              </div>
              <div className="flex items-center" data-id="element-761">
                <WavesIcon className="text-pink-300 mr-2" size={20} data-id="element-762" />
                <span className="text-white text-opacity-80" data-id="element-763">
                  Highly Sensitive
                </span>
              </div>
            </div>
            <input type="range" min="0" max="100" value={sensitivityLevel} onChange={handleSensitivityChange} className="mystical-slider w-full mb-4" data-id="element-764" />
            <div className="flex justify-between text-xs text-white text-opacity-60 mb-6" data-id="element-765">
              <span data-id="element-766">Grounded</span>
              <span data-id="element-767">Balanced</span>
              <span data-id="element-768">Receptive</span>
            </div>
            <div className="bg-white bg-opacity-20 p-4 rounded-lg" data-id="element-769">
              <h3 className="text-lg font-medium text-white mb-2" data-id="element-770">
                Your Sensitivity Profile
              </h3>
              <p className="text-white text-opacity-80" data-id="element-771">
                {getSensitivityDescription()}
              </p>
            </div>
          </div>
          <button onClick={handleContinue} className="w-full py-4 rounded-xl transition-all duration-300 text-white font-semibold bg-gradient-to-r from-pink-500 to-orange-500 hover:shadow-lg cursor-pointer mystical-button glow relative z-10" data-id="element-772">
            Continue Your Journey
          </button>
        </div>
      </div>
    </div>;
};
export default SensitivityAssessment;